/** Address fields shared by primary and billing addresses. */
export type Address = {
    streetAddress?: string;
    streetAddress2?: string;
    city?: string;
    state?: string;
    county?: string;
    zip?: string;
};

export type AddressType = 'primary' | 'billing';

/** The six tracked address field keys, used for extraction, validation, and error checking. */
export const ADDRESS_FIELDS = ['streetAddress', 'streetAddress2', 'city', 'state', 'county', 'zip'] as const;

/**
 * Formats address data into display lines for the read-only card.
 * Returns an array of 0–2 non-empty strings.
 */
export const getFormattedAddress = (address: Address): string[] => {
    return [
        [address.streetAddress, address.streetAddress2].filter(Boolean).join(', '),
        [address.city ? `${address.city},` : '', address.state, address.county, address.zip].filter(Boolean).join(' ')
    ].filter(Boolean);
};

/**
 * Extracts only the five tracked address fields from a broader object.
 * Used to normalise values before seeding the engine or comparing.
 */
export const extractAddressFields = (source: Address): Address =>
    Object.fromEntries(ADDRESS_FIELDS.map((field) => [field, source[field]])) as Address;

export const hasAddressData = (address: Address): boolean => ADDRESS_FIELDS.some((field) => Boolean(address[field]?.trim()));

/**
 * Returns true if the two address objects contain identical field values.
 * Uses JSON serialisation for a simple deep-equals without adding lodash.
 */
export const areAddressesEqual = (a: Address, b: Address): boolean =>
    JSON.stringify(extractAddressFields(a)) === JSON.stringify(extractAddressFields(b));

/** Returns true if any of the five tracked address fields carries an error. */
export const hasAddressErrors = (errors: Record<string, any> | undefined): boolean =>
    ADDRESS_FIELDS.some((field) => Boolean(errors?.[field]?.hasError));
